// Internal-link suggester.
//
// Two layers:
//
//   1) STATIC_KEYWORDS, hand-curated brand terms and canonical articles.
//      Source of truth. Imported synchronously, no network round-trip.
//
//   2) Dynamic keywords, derived from the CMS at runtime. The first call to
//      ``ensureDynamicKeywords()`` fetches the blog + KB indexes once and
//      extracts UK Companies House form codes (AP01, CH02, …) from each
//      article's title/slug. New articles auto-register without code changes.
//
// Render API: components that render article bodies call ``useInternalLinks()``
//, a hook that returns a ``linkify`` function bound to the latest merged
// keyword list. Re-renders when dynamic keywords arrive.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCmsApiBaseUrl } from "@/lib/cmsBase";

const API_BASE = getCmsApiBaseUrl();

// ---------------------------------------------------------------------------
// Static map, canonical brand terms + curated cross-links
// ---------------------------------------------------------------------------
export const STATIC_KEYWORDS = [
  // Longest phrases first
  { match: () => /\bCompanies House\b/g, href: "https://www.gov.uk/government/organisations/companies-house", external: true },
  { match: () => /\bCoSec App\b/gi, href: "/cosecapp" },
  { match: () => /\bCoVerify\b/g, href: "/coverify" },
  { match: () => /\bCoSearch\b/g, href: "/cosearch" },
  { match: () => /\bCoForm\b/g, href: "/coform" },
  { match: () => /\bECCTA\b/g, href: "/blog/what-changes-can-i-submit-to-companies-house-through-cs01" },
  { match: () => /\bconfirmation statement\b/gi, href: "/blog/what-is-a-confirmation-statement" },
  { match: () => /\bidentity verification\b/gi, href: "/kb/run-eccta-id-verification" },
  // Canonical form-code articles, these win over any auto-discovered duplicates.
  { match: () => /\bAP01\b/g, href: "/blog/companies-house-form-ap01-a-complete-guide-to-appointing-a-director" },
  { match: () => /\bAP02\b/g, href: "/blog/companies-house-form-ap02-your-ultimate-guide-to-appointing-a-corporate-director-in-the-uk" },
  { match: () => /\bAP03\b/g, href: "/blog/a-guide-for-ap03-form" },
  { match: () => /\bAP04\b/g, href: "/blog/a-guide-for-ap04-form" },
  { match: () => /\bAD01\b/g, href: "/blog/how-to-submit-ad01" },
  { match: () => /\bCS01\b/g, href: "/blog/what-is-a-confirmation-statement" },
  { match: () => /\bCH01\b/g, href: "/kb/how-to-change-the-director-details-ch01-in-e-co-sec" },
  { match: () => /\bCH02\b/g, href: "/kb/how-to-change-the-corporate-director-details-ch02-in-e-co-sec" },
  { match: () => /\bCH03\b/g, href: "/kb/how-to-change-the-secretary-details-ch03-in-e-co-sec" },
  { match: () => /\bCH04\b/g, href: "/kb/how-to-change-the-corporate-secretary-details-ch04-in-e-co-sec" },
];

// ---------------------------------------------------------------------------
// Dynamic discovery, extract form codes from CMS titles/slugs
// ---------------------------------------------------------------------------
// Form-code regex: covers the major UK Companies House filing prefixes plus
// PSC and SH share-related forms. New article titled "Form SH02 …" → "SH02"
// becomes an auto-registered link target.
const FORM_CODE_RE = /\b(?:AP|CH|AD|CS|SH|TM|NM|DS|MR|PSC|IN|OS|MG)\d{1,3}\b/g;

let _dynamicCache = null; // Promise<KeywordEntry[]>

function deriveKeywordsFromIndex(items, basePath) {
  const out = [];
  for (const it of items || []) {
    const attrs = it.attributes || it;
    const slug = attrs?.slug;
    const text = `${attrs?.title || ""} ${slug || ""}`;
    if (!slug) continue;
    const codes = new Set();
    let m;
    FORM_CODE_RE.lastIndex = 0;
    while ((m = FORM_CODE_RE.exec(text)) !== null) codes.add(m[0]);
    for (const code of codes) {
      // Word-boundary regex bound per entry; href to this article
      out.push({
        code,
        match: () => new RegExp(`\\b${code}\\b`, "g"),
        href: `${basePath}/${slug}`,
      });
    }
  }
  return out;
}

async function fetchDynamicKeywords() {
  try {
    const [blogRes, kbRes] = await Promise.all([
      fetch(`${API_BASE}/blog-posts?pagination[pageSize]=200&publicationState=live&populate=*`).then((r) => r.json()),
      fetch(`${API_BASE}/kb-articles?pagination[pageSize]=200&publicationState=live&populate=*`).then((r) => r.json()),
    ]);
    const derived = [
      ...deriveKeywordsFromIndex(blogRes?.data, "/blog"),
      ...deriveKeywordsFromIndex(kbRes?.data, "/kb"),
    ];
    // Dedup by code; STATIC_KEYWORDS canonical entries take precedence at link
    // time because they're listed first in the merged array.
    const seen = new Set();
    const filtered = [];
    for (const k of derived) {
      if (seen.has(k.code)) continue;
      seen.add(k.code);
      filtered.push(k);
    }
    return filtered;
  } catch (e) {
    // Network failure → fall back to static-only. Don't crash the article.
    return [];
  }
}

export function ensureDynamicKeywords() {
  if (!_dynamicCache) _dynamicCache = fetchDynamicKeywords();
  return _dynamicCache;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useInternalLinks() {
  const [dynamic, setDynamic] = useState([]);
  useEffect(() => {
    let mounted = true;
    ensureDynamicKeywords().then((kws) => {
      if (mounted) setDynamic(kws);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Merge, static first so canonical articles win.
  const all = [...STATIC_KEYWORDS, ...dynamic];

  return (text, opts) => linkifyWith(text, all, opts);
}

// ---------------------------------------------------------------------------
// Pure linkify, used by the hook
// ---------------------------------------------------------------------------
export function linkifyWith(text, keywords, { skipHref, key = "il" } = {}) {
  const used = new Set();
  if (skipHref) used.add(skipHref);
  return _walk(text, keywords, used, key);
}

function _walk(text, keywords, used, key) {
  if (!text || typeof text !== "string") return [text];

  // Find first matching keyword across all keywords; longest match wins on tie.
  const candidates = [];
  for (const entry of keywords) {
    if (used.has(entry.href)) continue;
    const re = entry.match();
    re.lastIndex = 0;
    const m = re.exec(text);
    if (m) candidates.push({ index: m.index, length: m[0].length, label: m[0], entry });
  }
  if (!candidates.length) return [text];
  candidates.sort((a, b) => a.index - b.index || b.length - a.length);
  const first = candidates[0];
  const before = text.slice(0, first.index);
  const after = text.slice(first.index + first.length);

  const linkEl = first.entry.external ? (
    <a
      key={`${key}-a`}
      href={first.entry.href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline decoration-accent/40 underline-offset-2 hover:decoration-accent hover:text-accent transition-colors"
      data-testid={`il-link-${first.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {first.label}
    </a>
  ) : (
    <Link
      key={`${key}-l`}
      to={first.entry.href}
      className="text-primary underline decoration-accent/40 underline-offset-2 hover:decoration-accent hover:text-accent transition-colors"
      data-testid={`il-link-${first.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {first.label}
    </Link>
  );

  used.add(first.entry.href);
  return [before, linkEl, ..._walk(after, keywords, used, `${key}-${first.index}`)];
}

// Back-compat for callers that still want a static-only linkify.
export function linkify(text, opts) {
  return linkifyWith(text, STATIC_KEYWORDS, opts);
}
